import { api, API_BASE } from '../../core/network/api';

export interface StatsOverview {
  players: { total: number; newToday: number; newThisWeek: number; online: number };
  economy: { totalCurrency: number; avgPerUser: number; marketVolume24h: number };
  activity: { total24h: number; pvp24h: number; pve24h: number };
  /**
   * Money in EUR cents (core's dashboard contract). `total`/`today` are
   * deprecated aliases of the *Cents fields — still cents, never major units,
   * which is why the widgets used to show 8000 for €80.00 (2026-10-19).
   */
  revenue: {
    totalCents: number; todayCents: number; currency: string;
    paymentsTotal?: number; paymentsToday?: number;
    total: number; today: number;
  };
  pending: { kyc: number; withdrawals: number };
  activityByHour: Array<{ hour: number; count: number }>;
  recentPlayers: Array<{ id: string; username: string | null; email: string; createdAt: string }>;
  recentActivity: Array<{ id: string; type: string; userId: string; createdAt: string }>;
}

export interface ServerStatus {
  uptime: number;
  memoryUsage: Record<string, number>;
  version: string;
}

export interface AdminUser {
  id: string;
  email: string;
  username: string | null;
  role: string;
  emailVerified: boolean;
  kycStatus: string;
  banned: boolean;
  banReason: string | null;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface AdminUserDetail extends AdminUser {
  firstName: string | null;
  lastName: string | null;
  locale: string | null;
  currency: string | null;
  softBalance: number;
  premiumBalance: number;
  billingLine1?: string | null;
  billingLine2?: string | null;
  billingCity?: string | null;
  billingZip?: string | null;
  billingCountry?: string | null;
}

/** Body of the game's `PATCH /admin/game-users/:id` — every key optional, '' clears. */
export type AdminUserPatch = Partial<{
  email: string; username: string; role: string; emailVerified: boolean; kycStatus: string;
  firstName: string; lastName: string; locale: string; currency: string;
  billingLine1: string; billingLine2: string; billingCity: string; billingZip: string; billingCountry: string;
}>;

export interface NewUserInput {
  email: string;
  password: string;
  username?: string;
  role?: string;
  emailVerified?: boolean;
  kycStatus?: string;
}

/**
 * The shape every paginated admin endpoint in core actually returns.
 *
 * Core is consistent about this — `admin/users`, `admin/transactions`,
 * `admin/invoices` and `admin/news` all end in `return { items, page,
 * pageSize, total }`. This panel was written against `{ data, total, page,
 * limit }`, which core never emits, so every list read `undefined` and threw.
 *
 * `pageSize` is the server's answer, not ours: two of the four endpoints fix
 * it at 20 and ignore anything the client asks for, so the page count has to
 * be derived from what came back.
 */
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * A row of core's `GET /admin/transactions` (`toTransactionRow`). `amount` is
 * MAJOR units (10 for €10.00, contract §3) and `amountCents` the stored minor
 * units — the table formatted `amount` as cents and showed €0.10 (2026-10-19).
 */
export interface TransactionRow {
  id: string;
  userId: string;
  username: string | null;
  email?: string | null;
  amountCents: number;
  /** Major units. Display `amountCents`; this exists for MAD. */
  amount: number;
  currency: string;
  provider: string;
  status: string;
  createdAt: string;
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
  sortOrder: number;
  active: boolean;
}

export interface LegalPage {
  id: string;
  slug: string;
  title: string;
  content: string;
}

/**
 * Mirrors core's `SocialLink` row. There is no `label` column: the admin form
 * used to send one, and core's whitelisting ValidationPipe answered every save
 * with `400 property label should not exist`.
 */
export interface SocialLink {
  id: string;
  platform: string;
  url: string;
  icon: string;
  sortOrder: number;
  active: boolean;
}

export type SocialLinkInput = Pick<SocialLink, 'platform' | 'url'> & Partial<Pick<SocialLink, 'sortOrder' | 'active'>>;

export interface NewsItem {
  id: string;
  slug: string;
  title: string;
  content: string;
  imageUrl: string | null;
  published: boolean;
  publishedAt?: string | null;
  createdAt: string;
}

/** A row of core's `GET /admin/invoices` — the Invoice model plus `user`. */
export interface InvoiceRow {
  id: string;
  number: string;
  type: string;
  amountCents: number;
  vatRate?: number;
  vatCents?: number;
  totalCents?: number;
  currency: string;
  /** PENDING | PAID | CANCELLED */
  status: string;
  description?: string;
  metadata?: Record<string, unknown> | null;
  recipientName?: string | null;
  recipientCompany?: string | null;
  recipientCode?: string | null;
  paymentMethod?: string | null;
  paidAt?: string | null;
  issuedAt?: string;
  createdAt: string;
  userId: string;
  user?: { email: string; username: string | null } | null;
  billingFirstName?: string | null;
  billingLastName?: string | null;
  billingLine1?: string | null;
  billingLine2?: string | null;
  billingCity?: string | null;
  billingZip?: string | null;
  billingCountry?: string | null;
  billingEmail?: string | null;
  paymentOrderRef?: string | null;
  withdrawalId?: string | null;
  creditNoteForInvoiceId?: string | null;
}

export type InvoicePatch = Partial<Record<
  | 'status' | 'description' | 'recipientName' | 'recipientCompany' | 'recipientCode' | 'paymentMethod'
  | 'billingFirstName' | 'billingLastName' | 'billingLine1' | 'billingLine2' | 'billingCity'
  | 'billingZip' | 'billingCountry' | 'billingEmail', string>>;

/** Core's canonical `POST /admin/invoices/generate` body. */
export interface GenerateInvoiceInput {
  userId: string;
  type: 'IN' | 'OUT';
  amountCents: number;
  description: string;
  currency?: string;
  vatRate?: number;
  status?: 'PAID' | 'PENDING';
  recipientName?: string;
  recipientCompany?: string;
  recipientCode?: string;
  paymentMethod?: string;
  idempotencyKey?: string;
}

export interface InvoiceSettings {
  prefix: string;
  creditNotePrefix: string;
  emailEnabled: boolean;
  redateBackdateDays: number;
  redateFutureHours: number;
}

export interface KycUser {
  id: string;
  email: string;
  username: string | null;
  kycStatus: string;
  kycFullName: string | null;
  kycCountry: string | null;
  kycSubmittedAt: string | null;
  kycReviewedAt?: string | null;
}

/**
 * A row of core's `GET /admin/withdrawals` — the WithdrawalRequest model with
 * `user` nested. There is no top-level `username`: reading one is what made
 * the Player column fall back to an id fragment (2026-10-19).
 */
export interface WithdrawalRow {
  id: string;
  userId: string;
  user?: { email: string; username: string | null } | null;
  email?: string | null;
  amountCents: number;
  currency: string;
  /** PENDING | PROCESSING | APPROVED | REJECTED | FAILED */
  status: string;
  payoutMethod: string | null;
  /** Set once core handed the payout to the gateway; check-status/resolve need it. */
  orderMerchantId?: string | null;
  reason?: string | null;
  createdAt: string;
}

/** What core's `POST /admin/withdrawals/:id/check-status` and `/resolve` report. */
export type WithdrawalSettleOutcome =
  | 'approved' | 'failed' | 'late_approved' | 'review' | 'duplicate' | 'pending' | 'noop' | 'not_found';

export interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface MetaValues {
  title: string | null;
  description: string | null;
  site_name: string | null;
  url: string | null;
  locale: string | null;
  image: string | null;
  image_alt: string | null;
  twitter_card: string | null;
  [key: string]: string | null | undefined;
}

export interface UserEvent {
  id: string;
  type: string;
  data: Record<string, unknown>;
  createdAt: string;
}

type QueryParams = Record<string, string | number | undefined>;

function qs(params: QueryParams): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

export const getStatsOverview = (): Promise<StatsOverview> =>
  api.get('/admin/stats/overview');

export const getServerStatus = (): Promise<ServerStatus> =>
  api.get('/admin/server-status');

export const getUsers = (params: QueryParams = {}): Promise<PaginatedResponse<AdminUser>> =>
  api.get(`/admin/users${qs(params)}`);

export const getUser = (id: string): Promise<AdminUserDetail> =>
  api.get(`/admin/users/${id}`);

export const banUser = (id: string, reason: string): Promise<void> =>
  api.post(`/admin/users/${id}/ban`, { reason });

export const unbanUser = (id: string): Promise<void> =>
  api.post(`/admin/users/${id}/unban`);

export const verifyEmail = (id: string): Promise<void> =>
  api.post(`/admin/users/${id}/verify-email`);

export const verifyKyc = (id: string, data: Record<string, unknown>): Promise<void> =>
  api.post(`/admin/users/${id}/verify-kyc`, data);

/** Core's create; it answers 409 when the email or username is taken. */
export const createUser = (data: NewUserInput): Promise<{ id: string; email: string; username: string | null; role: string }> =>
  api.post('/admin/users/create', data);

/** Game route — core has no user update (backend/src/game/admin). */
export const updateUser = (id: string, data: AdminUserPatch): Promise<AdminUserDetail> =>
  api.patch(`/admin/game-users/${encodeURIComponent(id)}`, data);

/** Core; also signs the player out everywhere. */
export const setUserPassword = (id: string, password: string): Promise<void> =>
  api.post(`/admin/users/${encodeURIComponent(id)}/password`, { password });

export const addCredits = (id: string, data: { amount: number; currency: string }): Promise<void> =>
  api.post(`/admin/users/${id}/add-credits`, data);

export const getTransactions = (params: QueryParams = {}): Promise<PaginatedResponse<TransactionRow>> =>
  api.get(`/admin/transactions${qs(params)}`);

/**
 * Core's CMS lists are paginated (`{ items, total, page, pageSize }`), like
 * every other admin list — the FAQ, Legal and Social sections were still
 * reading the response as a bare array. CMS collections are small, so one
 * full page is fetched and unwrapped here; a bare array is still accepted.
 */
const CMS_PAGE = '?per_page=100';
// Also used by API Keys, which is paginated the same way and had the same bug.

async function cmsList<T>(path: string): Promise<T[]> {
  const res = await api.get<PaginatedResponse<T> | T[]>(path + CMS_PAGE);
  return Array.isArray(res) ? res : (res?.items ?? []);
}

export const getFaqs = (): Promise<FaqItem[]> =>
  cmsList<FaqItem>('/admin/faq');

export const createFaq = (data: { question: string; answer: string }): Promise<FaqItem> =>
  api.post('/admin/faq', data);

export const updateFaq = (id: string, data: Partial<FaqItem>): Promise<FaqItem> =>
  api.patch(`/admin/faq/${id}`, data);

export const deleteFaq = (id: string): Promise<void> =>
  api.del(`/admin/faq/${id}`);

export const getLegalPages = (): Promise<LegalPage[]> =>
  cmsList<LegalPage>('/admin/legal');

export const getLegal = getLegalPages;

export const createLegal = (data: { slug: string; title: string; content: string }): Promise<LegalPage> =>
  api.post('/admin/legal', data);

export const updateLegal = (id: string, data: Partial<LegalPage>): Promise<LegalPage> =>
  api.patch(`/admin/legal/${id}`, data);

export const deleteLegal = (id: string): Promise<void> =>
  api.del(`/admin/legal/${id}`);

export const getSocialLinks = (): Promise<SocialLink[]> =>
  cmsList<SocialLink>('/admin/social-links');

export const createSocialLink = (data: SocialLinkInput): Promise<SocialLink> =>
  api.post('/admin/social-links', data);

export const updateSocialLink = (id: string, data: Partial<SocialLinkInput>): Promise<SocialLink> =>
  api.patch(`/admin/social-links/${id}`, data);

export const deleteSocialLink = (id: string): Promise<void> =>
  api.del(`/admin/social-links/${id}`);

export const getNews = (params: QueryParams = {}): Promise<PaginatedResponse<NewsItem>> =>
  api.get(`/admin/news${qs(params)}`);

export const createNews = (data: { title: string; content: string; slug?: string; published?: boolean; publishedAt?: string }): Promise<NewsItem> =>
  api.post('/admin/news', data);

export const updateNews = (id: string, data: Partial<NewsItem>): Promise<NewsItem> =>
  api.patch(`/admin/news/${id}`, data);

export const deleteNews = (id: string): Promise<void> =>
  api.del(`/admin/news/${id}`);

export const getSettings = (): Promise<Record<string, unknown>> =>
  api.get('/admin/settings');

/**
 * Core's `POST /admin/settings` sets ONE setting — `{ key, value }` with a
 * string value. The panel used to post the whole map, which core rejected
 * field by field. Changed keys are written one at a time, in order, so a
 * failure names the key it stopped at.
 */
export async function updateSettings(data: Record<string, unknown>): Promise<void> {
  for (const [key, value] of Object.entries(data)) {
    try {
      await api.post('/admin/settings', { key, value: String(value ?? '') });
    } catch (err) {
      if (err instanceof Error) err.message = `${key}: ${err.message}`;
      throw err;
    }
  }
}

export const getCompany = (): Promise<Record<string, unknown>> =>
  api.get('/admin/company');

export const updateCompany = (data: Record<string, unknown>): Promise<void> =>
  api.post('/admin/company', data);

export const getCommissions = (): Promise<Record<string, unknown>> =>
  api.get('/admin/commissions');

export const updateCommissions = (data: Record<string, unknown>): Promise<void> =>
  api.post('/admin/commissions', data);

export const getInvoices = (params: QueryParams = {}): Promise<PaginatedResponse<InvoiceRow>> =>
  api.get(`/admin/invoices${qs(params)}`);

export const generateInvoice = (data: GenerateInvoiceInput): Promise<InvoiceRow> =>
  api.post('/admin/invoices/generate', { idempotencyKey: crypto.randomUUID(), ...data });

/** Game route — recipient fields on any invoice, status/description on manual ones. */
export const updateInvoice = (number: string, data: InvoicePatch): Promise<InvoiceRow> =>
  api.patch(`/admin/game-invoices/${encodeURIComponent(number)}`, data);

/** Core's re-date: the document date, within the numbering-order window. */
export const redateInvoice = (number: string, issuedAt: string, reason: string): Promise<InvoiceRow> =>
  api.patch(`/admin/invoices/${encodeURIComponent(number)}/date`, { issuedAt, reason });

export const getInvoiceSettings = (): Promise<InvoiceSettings> =>
  api.get('/admin/invoices/settings');

export const updateInvoiceSettings = (data: Partial<InvoiceSettings>): Promise<InvoiceSettings> =>
  api.patch('/admin/invoices/settings', data);

/** Same-origin and cookie-authenticated, so a plain link downloads it. */
export const invoicePdfUrl = (ref: string): string =>
  `${API_BASE}/admin/invoices/${encodeURIComponent(ref)}/download`;

/**
 * Core's KYC list is paginated (`{ items, total, page, pageSize }`) like every
 * other admin list, and without `status` it returns everyone who ever
 * submitted. The panel typed it as a bare array and called `.filter` on the
 * envelope, so the KYC Queue tab threw on open (2026-10-19). Callers pass
 * `status` (any case) — omitted means every non-NONE status.
 */
export const getKycUsers = (params: QueryParams = {}): Promise<PaginatedResponse<KycUser>> =>
  api.get(`/admin/kyc/users${qs(params)}`);

export const approveKyc = (id: string): Promise<void> =>
  api.post(`/admin/kyc/users/${id}/approve`, { idempotencyKey: crypto.randomUUID() });

export const rejectKyc = (id: string, reason: string): Promise<void> =>
  api.post(`/admin/kyc/users/${id}/reject`, { reason, idempotencyKey: crypto.randomUUID() });

export const getWithdrawals = (params: QueryParams = {}): Promise<PaginatedResponse<WithdrawalRow>> =>
  api.get(`/admin/withdrawals${qs(params)}`);

export const approveWithdrawal = (id: string, note?: string): Promise<{ success: boolean }> =>
  api.post(`/admin/withdrawals/${id}/approve`, { note, idempotencyKey: crypto.randomUUID() });

export const rejectWithdrawal = (id: string, reason: string): Promise<{ success: boolean }> =>
  api.post(`/admin/withdrawals/${id}/reject`, { reason, idempotencyKey: crypto.randomUUID() });

/** Polls the payout gateway now; core settles the row if the gateway has a final state. */
export const checkWithdrawalStatus = (id: string): Promise<{ state: string | null; outcome: WithdrawalSettleOutcome }> =>
  api.post(`/admin/withdrawals/${encodeURIComponent(id)}/check-status`);

/**
 * Settles a stuck PROCESSING payout by hand after checking the gateway back
 * office. Core requires a 3–500 character note and only accepts PROCESSING.
 */
export const resolveWithdrawal = (id: string, state: 'APPROVED' | 'DECLINED', note: string): Promise<{ outcome: WithdrawalSettleOutcome }> =>
  api.post(`/admin/withdrawals/${encodeURIComponent(id)}/resolve`, { state, note });

export const getMeta = (): Promise<{ values: MetaValues; head: string }> =>
  api.get('/admin/meta');

/**
 * Core's bulk update takes `{ settings: { key: value } }` and stores each value
 * with `String(value)` — so the body is wrapped, and a cleared field is sent as
 * '' (a null would be saved as the literal text "null" in the page's <head>).
 */
export const updateMeta = (data: Record<string, string | null>): Promise<void> =>
  api.post('/admin/meta', {
    settings: Object.fromEntries(Object.entries(data).map(([k, v]) => [k, v ?? ''])),
  });

export const getApiKeys = (): Promise<ApiKeyRow[]> =>
  cmsList<ApiKeyRow>('/admin/api-keys');

export const createApiKey = (data: { name: string }): Promise<ApiKeyRow & { key: string }> =>
  api.post('/admin/api-keys', data);

export const deleteApiKey = (id: string): Promise<void> =>
  api.del(`/admin/api-keys/${id}`);

export const getUserEvents = (userId: string): Promise<UserEvent[]> =>
  api.get(`/admin/users/${userId}/events`);
